import React from "react";
import Checkbox from "../ui/Checkbox";
import sunriseIcon from "../../assets/sunriseicon.png";
import sunsetIcon from "../../assets/sunseticon.png";
import sunnyIcon from "../../assets/sunnydayicon.png";
import moonIcon from "../../assets/moonicon.png";

const stopsOptions = ["0", "1", "More than 2"];
const departureTimeSlots = ["00 - 12", "12 - 16", "16 - 20", "20 - 24"];
const departureIcons = [sunriseIcon, sunnyIcon, sunsetIcon, moonIcon];

interface AirlineOption {
  code: string;
  name: string;
}

interface FlightFilterSidebarProps {
  selectedStops: string[];
  setSelectedStops: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDepartureTimes: string[];
  setSelectedDepartureTimes: React.Dispatch<React.SetStateAction<string[]>>;
  selectedAirlines: string[];
  setSelectedAirlines: React.Dispatch<React.SetStateAction<string[]>>;
  duration: number;
  setDuration: React.Dispatch<React.SetStateAction<number>>;
  airlines: AirlineOption[];
}

const FlightFilterSidebar = ({
  selectedStops,
  setSelectedStops,
  selectedDepartureTimes,
  setSelectedDepartureTimes,
  selectedAirlines,
  setSelectedAirlines,
  duration,
  setDuration,
  airlines,
}: FlightFilterSidebarProps) => {
  const handleToggle = (
    value: string,
    selectedList: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(
      selectedList.includes(value)
        ? selectedList.filter((item) => item !== value)
        : [...selectedList, value]
    );
  };

  const formatDuration = (value: number) => {
    const hours = Math.floor(value);
    const minutes = (value - hours) * 60;
    return `${String(hours).padStart(2, "0")}:${minutes === 0 ? "00" : "30"} hrs`;
  };

  const timeLabels = ["Morning", "Noon", "Evening", "Night"];

  return (
    <div className="bg-white p-4 text-black space-y-5">
      {/* Stops */}
      <div className="space-y-2.5">
        <div className="pb-2 border-b border-gray-200">
          <span className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Stops</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {stopsOptions.map((label) => {
            const displayLabel = label === "0" ? "Non-stop" : label === "1" ? "1 Stop" : "2+ Stops";
            const isSelected = selectedStops.includes(label);
            return (
              <button
                key={label}
                onClick={() => handleToggle(label, selectedStops, setSelectedStops)}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                  isSelected
                    ? "bg-primary border-primary text-white"
                    : "bg-white border-gray-300 text-gray-600 hover:border-primary hover:text-primary"
                }`}
              >
                {displayLabel}
              </button>
            );
          })}
        </div>
      </div>

      {/* Departure Time */}
      <div className="space-y-2.5">
        <div className="pb-2 border-b border-gray-200">
          <span className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Departure Time</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {departureTimeSlots.map((label, index) => {
            const isSelected = selectedDepartureTimes.includes(label);
            return (
              <button
                key={label}
                onClick={() => handleToggle(label, selectedDepartureTimes, setSelectedDepartureTimes)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-all ${
                  isSelected
                    ? "bg-primary/10 border-primary text-primary"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:border-primary hover:text-primary"
                }`}
              >
                <img src={departureIcons[index]} alt="" className="w-5 h-5" />
                <span className="font-medium">{timeLabels[index]}</span>
                <span className="text-[10px] text-gray-400">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration */}
      <div className="space-y-2.5">
        <div className="pb-2 border-b border-gray-200">
          <span className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Duration</span>
        </div>
        <div className="pt-1 px-1">
          <input
            type="range"
            min={4}
            max={20}
            step={0.5}
            value={duration}
            onChange={(e) => setDuration(parseFloat(e.target.value))}
            className="w-full accent-[#0B2F9F] cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>4 hrs</span>
            <span>20 hrs</span>
          </div>
          <div className="text-center mt-2">
            <span className="text-xs font-semibold bg-gray-100 border border-gray-200 px-3 py-1 rounded-full inline-block text-gray-700">
              {formatDuration(duration)}
            </span>
          </div>
        </div>
      </div>

      {/* Airlines */}
      <div className="space-y-2.5">
        <div className="pb-2 border-b border-gray-200">
          <span className="font-semibold text-xs text-gray-500 uppercase tracking-wider">Airlines</span>
        </div>
        <div className="max-h-[200px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
          {airlines.map((airline) => (
            <label
              key={airline.code}
              className="flex items-center gap-2 cursor-pointer hover:text-[#0B2F9F] py-0.5"
            >
              <Checkbox
                id={`airline-${airline.code}`}
                checked={selectedAirlines.includes(airline.code)}
                onChange={() =>
                  handleToggle(airline.code, selectedAirlines, setSelectedAirlines)
                }
              />
              <span className="truncate text-xs text-gray-700">{airline.name}</span>
            </label>
          ))}
        </div>
        {airlines.length > 8 && (
          <div className="text-[#0B2F9F] text-xs font-medium cursor-pointer hover:underline pt-1">
            View more ↓
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightFilterSidebar;